import rollbar from 'shared-assets/src/errors/errors';
import sharedConfig from 'shared-assets/src/config-environments/config';
import { pick } from 'lodash';

const SEGMENTATION_USER_PROPERTIES = [
  'displayName',
  'email',
  'userId',
  'userType',
  'customerId',
  'practiceGuid',
  'companyType',
  'practiceSpecialty',
  'pricingFamily',
  'isSystemAdministrator',
  'isProvider',
  'isClinicalAssistant',
  'isOfficeStaff',
  'isBiller',
  'isBusinessManager',
  'pmEnabled',
  'isJointCustomer',
];

const FULLSTORY_USER_PROPERTIES_NAMES_MAPPING = {
  // System Properties (Don't need type suffix)
  // --------------------------------------------------------------------------
  displayName: 'displayName',
  email: 'email',
  // --------------------------------------------------------------------------
  userId: 'userId_int',
  userType: 'userType_str',
  customerId: 'customerId_int',
  practiceGuid: 'practiceGuid_str',
  companyType: 'companyType_str',
  practiceSpecialty: 'practiceSpecialty_str',
  pricingFamily: 'pricingFamily_str',
  isSystemAdministrator: 'isSystemAdministrator_bool',
  isProvider: 'isProvider_bool',
  isClinicalAssistant: 'isClinicalAssistant_bool',
  isOfficeStaff: 'isOfficeStaff_bool',
  isBiller: 'isBiller_bool',
  isBusinessManager: 'isBusinessManager_bool',
  pmEnabled: 'pmEnabled_bool',
  isJointCustomer: 'isJointCustomer_bool',
};

export const isFullstoryEnabled = () => sharedConfig?.fullstoryConfig?.enabled;

/**
 * Send information to identify the current session on FullStory
 * @param {Number} userId
 * @return void
 */
export const fsIdentify = userId => {
  try {
    if (isFullstoryEnabled()) {
      FS.identify(userId);
    }
  } catch (error) {
    rollbar.error(error);
  }
};

const convertToFullStoryPropertiesObject = userProperties => {
  let fullstoryProperties = {};
  if (!!userProperties) {
    for (const [propertyName, propertyValue] of Object.entries(
      userProperties,
    )) {
      Object.assign(fullstoryProperties, {
        [FULLSTORY_USER_PROPERTIES_NAMES_MAPPING[propertyName]]: propertyValue,
      });
    }
  }
  return fullstoryProperties;
};

/**
 * Add information about the current user
 * @param {String} displayName
 * @return void
 */
export const setFullStoryUser = userProperties => {
  try {
    if (isFullstoryEnabled()) {
      let validProperties = pick(userProperties, SEGMENTATION_USER_PROPERTIES);
      FS.setUserVars(convertToFullStoryPropertiesObject(validProperties));
    }
  } catch (error) {
    rollbar.error(error);
  }
};

/**
 * Indicates to fullstory that the current session has ended
 * @return void
 */
export const fullstoryAnonymize = () => {
  try {
    if (isFullstoryEnabled()) {
      FS.anonymize();
    }
  } catch (error) {
    rollbar.error(error);
  }
};

/**
 * Domain-specific events
 *
 * @param {Object} event - The event
 * @param {string} event.eventName - The event name
 * @param {Object} event.eventProperties - The event properties
 */
export const fullstoryEvent = event => {
  try {
    if (isFullstoryEnabled()) {
      FS.event(event.eventName, event.eventProperties);
    }
  } catch (error) {
    rollbar.error(error);
  }
};

/**
 * Capture information about the current page
 *
 * @param {Object} event - The event
 * @param {string} event.pageName - The page name
 * @param {Object} event.pageProperties - The page properties
 */
export const fullstoryPageVars = event => {
  try {
    if (isFullstoryEnabled()) {
      FS.setVars(event.pageName, event.pageProperties);
    }
  } catch (error) {
    rollbar.error(error);
  }
};
